import { useEffect, useState } from "react";

/**
 * Hook compartido del DS: devuelve `true` cuando el viewport está por
 * debajo del breakpoint mobile (768px por convención). Las primitivas
 * lo usan para conmutar layout (Drawer full-width, PageHeader compacto,
 * Card padding reducido, etc.).
 *
 * Se basa en `window.matchMedia` por dos razones:
 *  - Cero flicker tras montaje (solo una transición si el viewport
 *    cambia en runtime, p. ej. rotación de tablet).
 *  - No depende de `window.innerWidth` con resize listeners (más
 *    barato en repintados grandes).
 *
 * SSR-safe: durante la primera render en cliente devuelve `false`
 * (asume desktop). El primer effect corrige al instante con el valor
 * real, lo cual es aceptable porque el JS hidrata antes de que el
 * usuario perciba layout.
 *
 * @param {number} [maxPx=767] — px (inclusive) considerados mobile.
 */
export function useIsMobile(maxPx = 767) {
  const query = `(max-width: ${maxPx}px)`;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    // addEventListener es el API moderno; algunos browsers viejos
    // todavía exponen addListener — el fallback es trivial.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [query]);

  return isMobile;
}

export default useIsMobile;
